import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesktopOutlined, LoadingOutlined } from '@ant-design/icons';
import { CardActionMenu } from '../../../components/shared';

interface VMCardProps {
  id?: string;
  name: string;
  status: 'running' | 'stopped' | 'deploying' | 'error';
  realStatus?: string; // Estatus real del servicio
  os: string;
  osImage?: string; // URL de la imagen del OS
  instanceType: string;
  region: string;
  ipAddress?: string;
  createdAt: string;
  orderId?: string;
  onRefresh?: () => void; // Callback para actualizar la lista desde el componente padre
}

const VMCard: React.FC<VMCardProps> = ({
  id,
  name,
  status,
  realStatus,
  os,
  osImage,
  instanceType,
  region,
  ipAddress,
  createdAt,
  orderId,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);

  const statusConfig = {
    running: { color: 'bg-green-500', text: 'Running', textColor: 'text-green-700' },
    stopped: { color: 'bg-gray-500', text: 'Stopped', textColor: 'text-gray-700' },
    deploying: { color: 'bg-blue-500', text: 'Deploying', textColor: 'text-blue-700' },
    error: { color: 'bg-red-500', text: 'Error', textColor: 'text-red-700' },
  };

  const currentStatus = statusConfig[status];

  const detailRoute = orderId
    ? `/virtual-machines/${id}?orderId=${orderId}`
    : `/virtual-machines/${id}`;

  const handleClick = () => {
    if (id) navigate(detailRoute);
  };

  return (
    <div
      onClick={handleClick}
      className={`bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-all hover:border-gray-300 dark:hover:border-gray-600 group ${id ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          {osImage && !imageError ? (
            <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0">
              <img
                src={osImage}
                alt={os}
                className="w-full h-full object-contain p-1"
                onError={() => setImageError(true)}
              />
            </div>
          ) : (
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center text-white flex-shrink-0">
              <DesktopOutlined className="text-lg" />
            </div>
          )}
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
              {name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{os}</p>
          </div>
        </div>

        {id && (
          <CardActionMenu
            serviceId={id}
            status={status}
            realStatus={realStatus}
            detailRoute={detailRoute}
            serviceName={name}
            onRefresh={onRefresh}
          />
        )}
      </div>

      <div className="space-y-2 mb-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500 dark:text-gray-400">Instance Type</span>
          <span className="text-gray-900 dark:text-white font-medium">{instanceType}</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500 dark:text-gray-400">Region</span>
          <span className="text-gray-900 dark:text-white font-medium">{region}</span>
        </div>
        {ipAddress && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 dark:text-gray-400">IP Address</span>
            <span className="text-gray-900 dark:text-white font-medium font-mono">{ipAddress}</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2">
          {status === 'deploying' ? (
            <LoadingOutlined spin className={`text-xs ${currentStatus.textColor} dark:text-blue-400`} />
          ) : (
            <div className={`w-2 h-2 ${currentStatus.color} rounded-full`}></div>
          )}
          <span className={`text-xs font-medium ${currentStatus.textColor} dark:text-gray-300`}>
            {realStatus ? realStatus.charAt(0).toUpperCase() + realStatus.slice(1).toLowerCase() : currentStatus.text}
          </span>
        </div>

        <span className="text-xs text-gray-500 dark:text-gray-400">Created {createdAt}</span>
      </div>
    </div>
  );
};

export default VMCard;
